import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../context/AuthContext";
import {
	COUNTRIES,
	VAT_TREATMENT_LABELS,
	inferVatTreatment,
	validateTaxIdLocally,
} from "../../utils/countries";
import Button from "../Button/Button";
import InputText from "../Input/InputText";
import Typography from "../Typography";
import CloseIcon from "../Icons/CloseIcon";
import CheckCircleIcon from "../Icons/CheckCircleIcon";
import ChevronDownIcon from "../Icons/ChevronDownIcon";
import { theme } from "../../theme";

/**
 * Reusable fiscal-profile form. Used in two places:
 *   - As the first step of `StripeOnboardingModal` (before connecting Stripe).
 *   - As a "Facturación" section in `UserSettings` for later edits.
 *
 * Props:
 *   - initialValues?: { legalName, taxId, billingCountry, vatTreatment, billingAddress }
 *   - onSuccess?:    () => void           Called after PUT succeeds.
 *   - onCancel?:     () => void           Optional. Renders a Cancel button.
 *   - submitLabel?:  string               Defaults to "Guardar y continuar".
 *   - title?:        string               Optional heading rendered above the form.
 *   - description?:  string               Optional helper text below the heading.
 */
const BillingProfileForm = ({
	initialValues = {},
	onSuccess,
	onCancel,
	submitLabel,
	title,
	description,
}) => {
	const { t } = useTranslation("userProfile");
	const { refreshUserData } = useAuth();

	const [legalName, setLegalName] = useState(initialValues.legalName || "");
	const [taxId, setTaxId] = useState(initialValues.taxId || "");
	const [billingCountry, setBillingCountry] = useState(
		initialValues.billingCountry || "ES",
	);
	const [outsideVat, setOutsideVat] = useState(
		initialValues.vatTreatment === "es_territory_outside_vat",
	);
	const [billingAddress, setBillingAddress] = useState(
		initialValues.billingAddress || "",
	);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});

	const resolvedSubmitLabel = submitLabel || t("billingProfileForm.submit");
	const useSplitLayout = Boolean(title || description || onCancel);

	// Derived — never stored as separate state to avoid desync
	const vatTreatment =
		billingCountry === "ES" && outsideVat
			? "es_territory_outside_vat"
			: inferVatTreatment(billingCountry);

	// Reset the outside-VAT checkbox when the user switches away from Spain
	useEffect(() => {
		if (billingCountry !== "ES") setOutsideVat(false);
	}, [billingCountry]);

	const taxIdHelp = useMemo(() => {
		if (!taxId) return null;
		const result = validateTaxIdLocally({ taxId, country: billingCountry });
		if (result.reason === "no_pattern") {
			return {
				tone: "info",
				text: t("billingProfileForm.taxIdHelp.noPattern"),
			};
		}
		if (!result.valid) {
			return { tone: "error", text: t("billingProfileForm.taxIdHelp.invalid") };
		}
		return { tone: "ok", text: t("billingProfileForm.taxIdHelp.valid") };
	}, [taxId, billingCountry, t]);

	const validate = () => {
		const next = {};
		if (!legalName || legalName.trim().length < 2) {
			next.legalName = t("billingProfileForm.errors.legalName");
		}
		if (!billingCountry) {
			next.billingCountry = t("billingProfileForm.errors.billingCountry");
		}
		if (!billingAddress || billingAddress.trim().length < 5) {
			next.billingAddress = t("billingProfileForm.errors.billingAddress");
		}
		if (!taxId || taxId.trim().length < 3) {
			next.taxId = t("billingProfileForm.errors.taxIdRequired");
		} else {
			const result = validateTaxIdLocally({ taxId, country: billingCountry });
			if (!result.valid && result.reason !== "no_pattern") {
				next.taxId = t("billingProfileForm.errors.taxIdInvalid");
			}
		}
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (submitting) return;
		if (!validate()) return;

		setSubmitting(true);
		try {
			await axiosClient.put("/api/users/profile", {
				legalName: legalName.trim(),
				taxId: taxId.trim().toUpperCase(),
				billingCountry,
				vatTreatment,
				billingAddress: billingAddress.trim(),
			});
			await refreshUserData();
			toast.success(t("billingProfileForm.toasts.success"));
			onSuccess?.();
		} catch (err) {
			console.error("[BillingProfileForm] PUT /api/users/profile failed", err);
			const apiError = err?.response?.data;
			if (apiError?.error === "INVALID_TAX_ID") {
				setErrors({ taxId: t("billingProfileForm.errors.taxIdInvalid") });
			} else {
				toast.error(apiError?.message || t("billingProfileForm.toasts.error"));
			}
		} finally {
			setSubmitting(false);
		}
	};

	const formContent = (
		<>
			{!useSplitLayout && title && (
				<Typography as="h3" $variant="title-5-semibold">
					{title}
				</Typography>
			)}
			{!useSplitLayout && description && (
				<Typography as="p" $variant="body-2-regular">
					{description}
				</Typography>
			)}

			<Field>
				<Label htmlFor="legalName">
					{t("billingProfileForm.fields.legalName.label")}
				</Label>
				<InputText
					id="legalName"
					type="text"
					value={legalName}
					onChange={(e) => setLegalName(e.target.value)}
					placeholder={t("billingProfileForm.fields.legalName.placeholder")}
					$size="large"
					$variant={errors.legalName ? "error" : "default"}
					required
				/>
				<HelpText>{t("billingProfileForm.fields.legalName.help")}</HelpText>
				{errors.legalName && <ErrorText>{errors.legalName}</ErrorText>}
			</Field>

			<Field>
				<Label htmlFor="billingCountry">
					{t("billingProfileForm.fields.billingCountry.label")}
				</Label>
				<CountryVatRow $hasError={Boolean(errors.billingCountry)}>
					<SelectWrapper>
						<StyledSelect
							id="billingCountry"
							value={billingCountry}
							onChange={(e) => setBillingCountry(e.target.value)}
							required
						>
							{COUNTRIES.map((c) => (
								<option key={c.code} value={c.code}>
									{c.name}
								</option>
							))}
						</StyledSelect>
						<SelectChevron>
							<ChevronDownIcon
								width={20}
								height={20}
								fill={theme.colors.defaultWeak}
							/>
						</SelectChevron>
					</SelectWrapper>
					<VatDisplay>
						<Typography
							$variant="body-1-regular"
							color={theme.colors.defaultWeak}
						>
							{VAT_TREATMENT_LABELS[vatTreatment] || vatTreatment}
						</Typography>
					</VatDisplay>
				</CountryVatRow>
				{errors.billingCountry && (
					<ErrorText>{errors.billingCountry}</ErrorText>
				)}
				{billingCountry === "ES" && (
					<CheckboxRow>
						<CheckboxInput
							id="outsideVat"
							type="checkbox"
							checked={outsideVat}
							onChange={(e) => setOutsideVat(e.target.checked)}
						/>
						<CheckboxLabel htmlFor="outsideVat">
							{t("billingProfileForm.fields.vatTreatment.outsideVat")}
						</CheckboxLabel>
					</CheckboxRow>
				)}
			</Field>

			<Field>
				<Label htmlFor="taxId">
					{t("billingProfileForm.fields.taxId.label")}
				</Label>
				<TaxIdWrapper>
					<InputText
						id="taxId"
						type="text"
						value={taxId}
						onChange={(e) => setTaxId(e.target.value.toUpperCase())}
						placeholder={
							billingCountry === "ES"
								? t("billingProfileForm.fields.taxId.placeholderEs")
								: t("billingProfileForm.fields.taxId.placeholderDefault")
						}
						$size="large"
						$variant={errors.taxId ? "error" : "default"}
						required
					/>
					{taxIdHelp?.tone === "ok" && (
						<TaxIdCheck>
							<CheckCircleIcon
								width={20}
								height={20}
								fill={theme.colors.successMain}
							/>
						</TaxIdCheck>
					)}
				</TaxIdWrapper>
				{errors.taxId && <ErrorText>{errors.taxId}</ErrorText>}
				{!errors.taxId && taxIdHelp && taxIdHelp.tone !== "ok" && (
					<HelpText $tone={taxIdHelp.tone}>{taxIdHelp.text}</HelpText>
				)}
			</Field>

			<Field>
				<Label htmlFor="billingAddress">
					{t("billingProfileForm.fields.billingAddress.label")}
				</Label>
				<InputText
					id="billingAddress"
					type="text"
					value={billingAddress}
					onChange={(e) => setBillingAddress(e.target.value)}
					placeholder={t(
						"billingProfileForm.fields.billingAddress.placeholder",
					)}
					$size="large"
					$variant={errors.billingAddress ? "error" : "default"}
					required
				/>
				<HelpText>
					{t("billingProfileForm.fields.billingAddress.help")}
				</HelpText>
				{errors.billingAddress && (
					<ErrorText>{errors.billingAddress}</ErrorText>
				)}
			</Field>
		</>
	);

	if (useSplitLayout) {
		return (
			<Layout>
				<DescriptionPanel>
					{onCancel && (
						<CloseArea>
							<CloseButton
								type="button"
								onClick={onCancel}
								aria-label={t("billingProfileForm.close")}
							>
								<CloseIcon
									width={20}
									height={20}
									fill={theme.colors.defaultMain}
								/>
							</CloseButton>
						</CloseArea>
					)}
					<DescriptionContent>
						<Typography as="p" $variant="body-1-medium">
							{t("billingProfileForm.sidebar.title")}
						</Typography>
						<Typography
							as="p"
							$variant="body-2-regular"
							color={theme.colors.defaultWeak}
						>
							{description || t("billingProfileForm.sidebar.body1")}
						</Typography>
						<Typography
							as="p"
							$variant="body-2-regular"
							color={theme.colors.defaultWeak}
						>
							{t("billingProfileForm.sidebar.body2")}
						</Typography>
					</DescriptionContent>
				</DescriptionPanel>

				<FormSection onSubmit={handleSubmit} noValidate>
					<FormHeader>
						<Typography as="h3" $variant="title-5-medium">
							{title || t("billingProfileForm.defaultTitle")}
						</Typography>
					</FormHeader>
					<FormFields>{formContent}</FormFields>
					<FormFooter>
						<Button type="submit" size="lg" disabled={submitting}>
							{submitting
								? t("billingProfileForm.saving")
								: resolvedSubmitLabel}
						</Button>
					</FormFooter>
				</FormSection>
			</Layout>
		);
	}

	return (
		<Form onSubmit={handleSubmit} noValidate>
			{formContent}
			<Actions>
				{onCancel && (
					<Button
						type="button"
						$variant="outline"
						size="lg"
						onClick={onCancel}
						disabled={submitting}
					>
						{t("billingProfileForm.cancel")}
					</Button>
				)}
				<Button type="submit" size="lg" disabled={submitting}>
					{submitting ? t("billingProfileForm.saving") : resolvedSubmitLabel}
				</Button>
			</Actions>
		</Form>
	);
};

export default BillingProfileForm;

const Layout = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 480px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const DescriptionPanel = styled.aside`
  width: 220px;
  flex-shrink: 0;
  padding: 12px 8px;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const CloseArea = styled.div`
  padding-bottom: 12px;
  padding-left: 4px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const DescriptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px;
`;

const FormSection = styled.form`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${({ theme }) => theme.fill.defaultMain};
`;

const FormHeader = styled.div`
  padding-bottom: 24px;
`;

const FormFields = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.defaultMain};
`;

const CountryVatRow = styled.div`
  display: flex;
  border: 1px solid ${({ $hasError, theme }) =>
		$hasError ? theme.border.errorMain : theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: ${({ theme }) => theme.sizing.xxl};
  padding: 0 ${({ theme }) => theme.sizing.sm};
  padding-right: 40px;
  border: none;
  background: ${({ theme }) => theme.fill.defaultMain};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.defaultStrong};
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`;

const SelectChevron = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  pointer-events: none;
`;

const VatDisplay = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 8px ${({ theme }) => theme.sizing.sm};
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border-left: 1px solid ${({ theme }) => theme.border.defaultWeak};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 48px;
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.errorMain};
`;

const TaxIdWrapper = styled.div`
  position: relative;
`;

const TaxIdCheck = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  pointer-events: none;
`;

const HelpText = styled.span`
  font-size: 12px;
  color: ${({ $tone, theme }) =>
		$tone === "error" ? theme.colors.errorMain : theme.colors.defaultWeak};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 4px;
`;

const CheckboxInput = styled.input`
  margin-top: 2px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.brandMain};
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.defaultWeak};
  line-height: 1.4;
  cursor: pointer;
`;
